import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import styles from './CompaniesNetworkButton.module.scss';
import { fetchCompaniesNetworkData, fetchLenderConnectionsData } from '../../../../store/dashboard/overview/overview_apiHelper';
import { ALERT_ICON_DISABLED, blackArrowIcon } from '../../../../assets/hostedassets';
import { getDealFormConfig } from '../../../../store/investor/apiHelpers';
import { useAppSelector } from '../../../../app/hooks';
import { VIEW_CONFIGS } from '../../../../utils/constants/commonConstants';
import { CompaniesNetworkData, StatusOption } from '../../DashboardModules/Overview/components/RelatedCompaniesNetwork/types';
import RelatedCompaniesNetworkDrawer from '../../DashboardModules/Overview/components/RelatedCompaniesNetwork/RelatedCompaniesNetworkDrawer';
import clsx from 'clsx';
import { Mixpanel } from '../../../../utils/mixpanel';
import { AICA_NETWORK_DRAWER_OPENED } from '../../../../utils/constants/mixpanelEvents/investorEvents';

interface CompaniesNetworkButtonProps {
    companyName?: string;
    isRelatedPartiesLocked?: boolean;
}

const CompaniesNetworkButton = ({ companyName, isRelatedPartiesLocked = false }: CompaniesNetworkButtonProps) => {
    const params = useParams();
    const assesseeOrgId = params?.id;

    const userData = useAppSelector((state) => state.user.userData);
    const investorConfig = useAppSelector((state) => state.investor.config);

    const [data, setData] = useState<CompaniesNetworkData | null>(null);
    const [lenderConnections, setLenderConnections] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [localStatusOptions, setLocalStatusOptions] = useState<Record<string, StatusOption>>({});
    const [localRelationshipStatusOptions, setLocalRelationshipStatusOptions] = useState<Record<string, StatusOption>>({});

    useEffect(() => {
        if (!assesseeOrgId) return;
        let cancelled = false;
        setIsLoading(true);
        Promise.all([fetchCompaniesNetworkData({ orgId: assesseeOrgId }), fetchLenderConnectionsData({ orgId: assesseeOrgId })])
            .then(([networkResult, lenders]) => {
                if (!cancelled) {
                    setData(networkResult ?? null);
                    setLenderConnections(lenders || []);
                }
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [assesseeOrgId, fetchCompaniesNetworkData, setData, setIsLoading]);

    const extractStatusOptions = (config: any, field: string): Record<string, StatusOption> => {
        const columns = config?.leadsColumns?.columns || [];
        const col = columns.find((c: any) => c?.field === field);
        return col?.dropdownOptions || {};
    };

    const configStatusOptions = extractStatusOptions(investorConfig?.additionalDetailsConfig, 'companyStatus');
    const configRelationshipStatusOptions = extractStatusOptions(
        investorConfig?.additionalDetailsConfig,
        'customFields.relationshipStatus',
    );

    const statusOptionsSource = Object.keys(localStatusOptions).length > 0 ? localStatusOptions : configStatusOptions;
    const relationshipStatusOptionsSource =
        Object.keys(localRelationshipStatusOptions).length > 0 ? localRelationshipStatusOptions : configRelationshipStatusOptions;

    const statusOptions = useMemo<StatusOption[]>(() => Object.values(statusOptionsSource), [statusOptionsSource]);
    const relationshipStatusOptions = useMemo<StatusOption[]>(
        () => Object.values(relationshipStatusOptionsSource),
        [relationshipStatusOptionsSource],
    );

    useEffect(() => {
        const hasOptions = statusOptions.length > 0 || relationshipStatusOptions.length > 0;
        if (isLoading || hasOptions) return;
        if (!userData?.accountType || !userData?.investorOrgId || !userData?.primaryEmail) return;

        getDealFormConfig(
            {
                accountType: userData.accountType,
                investorOrgId: userData.investorOrgId,
                primaryEmail: userData.primaryEmail,
                configName: VIEW_CONFIGS.AICA_DEAL_NAVBAR_VIEW,
            },
            {
                onSuccess: (additionalDetailsConfig) => {
                    setLocalStatusOptions(extractStatusOptions(additionalDetailsConfig, 'companyStatus'));
                    setLocalRelationshipStatusOptions(extractStatusOptions(additionalDetailsConfig, 'customFields.relationshipStatus'));
                },
            },
        );
    }, [
        isLoading,
        statusOptions.length,
        relationshipStatusOptions.length,
        userData?.accountType,
        userData?.investorOrgId,
        userData?.primaryEmail,
    ]);

    const relatedPartiesCount = Object.keys(data?.networkRelatedPartyEntities ?? {}).length;
    const totalCount = (data?.totalCompaniesInNetwork ?? 0) + (lenderConnections?.length ?? 0) + relatedPartiesCount;
    const canOpen = !isLoading && data !== null;

    const handleOpenDrawer = () => {
        setIsDrawerOpen(true);
        Mixpanel.track(AICA_NETWORK_DRAWER_OPENED, {
            userEmail: userData?.primaryEmail,
            companyName,
            pnoId: userData?.investorOrgId,
            totalConnectionsCount: totalCount,
            source: 'sidebar',
        });
    };

    return (
        <>
            <div className={styles.ConnectionsBtnContainer}>
                <button className={styles.ConnectionsBtn} onClick={handleOpenDrawer} disabled={!canOpen}>
                    <div>{isLoading ? 'Loading' : totalCount} Connections</div>
                    <img src={canOpen ? blackArrowIcon : ALERT_ICON_DISABLED} alt="" />
                </button>
            </div>

            {isDrawerOpen && data && (
                <RelatedCompaniesNetworkDrawer
                    data={data}
                    lenderConnections={lenderConnections}
                    onClose={() => setIsDrawerOpen(false)}
                    statusOptions={statusOptions}
                    relationshipStatusOptions={relationshipStatusOptions}
                    companyName={companyName}
                    isRelatedPartiesLocked={isRelatedPartiesLocked}
                    assesseeOrgId={assesseeOrgId}
                />
            )}
        </>
    );
};

export default CompaniesNetworkButton;
